import { createSlice } from '@reduxjs/toolkit'
import { getRuntimeErrorKey } from '../../lib/runtimeErrorMessages'

function findStreamingIndex(transcript, userText) {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const entry = transcript[i]
    if (entry?.isStreaming && entry?.userText === userText) return i
  }
  return -1
}

export const botSlice = createSlice({
  name: 'bot',
  initialState: {
    currentSession: null,
    currentTranscript: [],
    sessions: [],
    requestInFlight: false,
    /** Bumped when the user leaves a chat mid-response, so a stale stream cannot unlock a newer one. */
    streamGeneration: 0
  },
  reducers: {
    currentUpdated: (state, action) => {
      return {
        ...state,
        currentSession: action.payload.currentSession,
        currentTranscript: action.payload.currentTranscript,
      }
    },
    sessionsUpdated: (state, action) => {
      return {
        ...state,
        sessions: action.payload.sessions,
        currentSession: action.payload.currentSession,
        currentTranscript: action.payload.currentTranscript,
      }
    },
    sessionsListUpdated: (state, action) => ({
      ...state,
      sessions: action.payload.sessions,
    }),
    requestStarted: (state, action) => {
      if (action.payload !== state.streamGeneration) return
      state.requestInFlight = true
    },
    requestFinished: (state, action) => {
      if (action.payload !== state.streamGeneration) return
      state.requestInFlight = false
    },
    /** Drop the in-flight lock without touching another bot's later request. */
    abandonPendingResponse: (state) => {
      state.streamGeneration += 1
      state.requestInFlight = false
    },
    /** Replace the visible transcript. Does not change the send lock. */
    userMessageAppended: (state, action) => {
      return {
        ...state,
        currentTranscript: action.payload.currentTranscript,
      }
    },
    startStreamingResponse: (state, action) => {
      const { userText } = action.payload || {}
      if (!userText) return

      state.currentTranscript.push({
        sender: 'MODEL',
        text: '',
        isStreaming: true,
        streamThinking: '',
        userText,
      })
    },
    appendStreamDelta: (state, action) => {
      const { userText, streamType, text } = action.payload || {}
      if (!userText || !text) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.isStreaming = true
      if (streamType === 'thinking_delta') {
        entry.streamThinking = (entry.streamThinking || '') + text
      } else if (streamType === 'message_delta') {
        entry.text = (entry.text || '') + text
      }
    },
    resetStreamBuffers: (state, action) => {
      const { userText } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.isStreaming = true
      entry.streamThinking = ''
      entry.text = ''
    },
    finalizeStreamingResponse: (state, action) => {
      const { userText, modelMessage } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      if (modelMessage) {
        entry.text = modelMessage
      }
      delete entry.isStreaming
      delete entry.streamThinking
      delete entry.userText
    },
    markStreamingFailed: (state, action) => {
      const { userText, errorCode, source } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.errorKey = getRuntimeErrorKey(errorCode, source)
      delete entry.isStreaming
      delete entry.streamThinking
      delete entry.userText
    },
    sessionNameUpdated: (state, action) => {
      const { id, name } = action.payload || {}
      if (!id || name == null) return
      const session = state.sessions.find((s) => s.id === id)
      if (session) session.name = name
    },
  },
})

export const {
  currentUpdated,
  sessionsUpdated,
  sessionsListUpdated,
  requestStarted,
  requestFinished,
  abandonPendingResponse,
  userMessageAppended,
  startStreamingResponse,
  appendStreamDelta,
  resetStreamBuffers,
  finalizeStreamingResponse,
  markStreamingFailed,
  sessionNameUpdated,
} = botSlice.actions

export default botSlice.reducer
